import { useState, useEffect, useRef } from "react";
import { supabase } from '../../supabase';
import { RunDownEditor, RunDownViewer } from './RunDown';
import { ShowProgramEditor, ShowProgramViewer } from './ShowProgram';
import { FilesEditor } from './FilesEditor';
import { TodoEditor } from './TodoEditor';
import { RehearsalEditor } from './RehearsalEditor';
import { TravelEditor } from './TravelEditor';
import { logActivity } from '../ActivityLog';
import { CustomerSelect } from '../Customers';

const C = {
  bg:"#0e0e0e", surface:"#181818", surface2:"#202020",
  border:"#2a2a2a", muted:"#666", orange:"#FF5F1F", magenta:"#E8197D",
  gold:"#F5A623", green:"#4ade80", white:"#fff", red:"#f87171", blue:"#60a5fa",
};

const card    = (x={}) => ({ background:C.surface, borderRadius:10, padding:16, border:`0.5px solid ${C.border}`, ...x });
const inp     = (x={}) => ({ background:C.surface2, border:`0.5px solid ${C.border}`, borderRadius:8, padding:"9px 12px", color:C.white, fontSize:16, outline:"none", width:"100%", boxSizing:"border-box", ...x });
const btn     = (v="primary",x={}) => ({ padding:"8px 16px", borderRadius:8, fontSize:13, fontWeight:500, cursor:"pointer", border:"none", boxSizing:"border-box", ...(v==="primary"?{background:`linear-gradient(135deg,${C.orange},${C.magenta})`,color:C.white,border:"none"}:{}), ...(v==="ghost"?{background:"none",border:`0.5px solid ${C.border}`,color:C.muted}:{}), ...(v==="success"?{background:"#14532d",color:C.green,border:`0.5px solid #4ade8033`}:{}), ...(v==="danger"?{background:"#3d1515",color:C.red,border:`0.5px solid #f8717133`}:{}), ...(v==="warn"?{background:"#3d2e0a",color:C.gold,border:`0.5px solid ${C.gold}44`}:{}), ...x });
const lbl     = t => <div style={{fontSize:11,color:C.muted,marginBottom:5,textTransform:"uppercase",letterSpacing:0.5}}>{t}</div>;
const gradBar = <div style={{height:2,background:`linear-gradient(90deg,${C.orange},${C.magenta},${C.gold})`,borderRadius:1,marginBottom:24}}/>;
const badge   = s => {
  const statusCfg = {
    confirmed:  { label:"Bekreftet",   color:C.green,   bg:"#14532d44", border:"#4ade8033" },
    inquiry:    { label:"Forespørsel", color:C.magenta, bg:"#E8197D22", border:"#E8197D44" },
    cancelled:  { label:"Kansellert",  color:C.red,     bg:"#3d151522", border:"#f8717133" },
  };
  return { padding:"3px 10px", borderRadius:20, fontSize:11, fontWeight:500, color:statusCfg[s]?.color, background:statusCfg[s]?.bg, border:`0.5px solid ${statusCfg[s]?.border}`, whiteSpace:"nowrap" };
};
const ibadge  = s => {
  const inviteCfg = { accepted:{ label:"Akseptert", color:C.green }, pending:{ label:"Venter", color:C.gold }, declined:{ label:"Avvist", color:C.red } };
  return { padding:"2px 8px", borderRadius:20, fontSize:11, fontWeight:500, color:inviteCfg[s]?.color, background:`${inviteCfg[s]?.color}22`, border:`0.5px solid ${inviteCfg[s]?.color}44` };
};

const statusCfg = {
  confirmed:  { label:"Bekreftet",   color:C.green,   bg:"#14532d44", border:"#4ade8033" },
  inquiry:    { label:"Forespørsel", color:C.magenta, bg:"#E8197D22", border:"#E8197D44" },
  cancelled:  { label:"Kansellert",  color:C.red,     bg:"#3d151522", border:"#f8717133" },
};
const inviteCfg = {
  accepted:{ label:"Akseptert", color:C.green },
  pending: { label:"Venter",    color:C.gold  },
  declined:{ label:"Avvist",    color:C.red   },
};
const roleCfg = {
  admin:      { label:"Admin",      color:"#34d399" },
  danser:     { label:"Danser",     color:C.magenta },
  vokalist:   { label:"Vokalist",   color:"#a78bfa" },
  tm:         { label:"TM",         color:C.gold    },
  akrobat:    { label:"Akrobat",    color:C.orange  },
  musiker:    { label:"Musiker",    color:C.blue    },
  tekniker:   { label:"Tekniker",   color:"#94a3b8" },
};
const MODULE_CFG = {
  rehearsals:   { label:"Øvingstider",         icon:"🕐", adminOnly:false },
  showprogram:  { label:"Show-program",         icon:"🎭", adminOnly:false },
  travel:       { label:"Reise og transport",   icon:"✈️", adminOnly:false },
  accommodation:{ label:"Overnatting",          icon:"🏨", adminOnly:false },
  costume:      { label:"Kostyme",              icon:"👗", adminOnly:false },
  contract:     { label:"Kontrakt og betaling", icon:"📋", adminOnly:true  },
};

function can(user, permission) {
  if(user?.role === "admin") return true;
  return user?.permissions?.[permission] === true;
}

function createNotification(type, project, recipientIds, extra={}) {
  return { type, project_id:project.id, project_name:project.name, recipient_ids:recipientIds, read:{}, ...extra };
}

function Avatar({ initials, size=36, role }) {
  const color = roleCfg[role]?.color || C.orange;
  return <div style={{width:size,height:size,borderRadius:"50%",background:`${color}22`,border:`1px solid ${color}55`,display:"flex",alignItems:"center",justifyContent:"center",fontSize:size>30?13:10,color,fontWeight:500,flexShrink:0}}>{initials}</div>;
}

function Toast({ msg, ok=true }) {
  if(!msg) return null;
  return <div style={{position:"fixed",bottom:24,left:"50%",transform:"translateX(-50%)",background:ok?"#14532d":"#3d1515",color:ok?C.green:C.red,padding:"10px 20px",borderRadius:8,fontSize:13,zIndex:999,border:`0.5px solid ${ok?"#4ade8033":"#f8717133"}`}}>{msg}</div>;
}

function useToast() {
  const [toast,setToast] = useState({msg:"",ok:true});
  const showToast = (msg,ok=true) => { setToast({msg,ok}); setTimeout(()=>setToast({msg:"",ok:true}),2500); };
  return [toast,showToast];
}

function AdminNotesView({ notes }) {
  const [expanded, setExpanded] = useState(false);
  const [overflow, setOverflow] = useState(false);
  const ref = useRef(null);

  useEffect(()=>{
    if(ref.current) setOverflow(ref.current.scrollHeight > ref.current.clientHeight);
  },[notes]);

  const rendered = (notes||"Ingen notater enda.").split(/(\*\*.*?\*\*)/).map((part,i)=>
    part.startsWith("**")&&part.endsWith("**")
      ?<strong key={i} style={{color:"#fff"}}>{part.slice(2,-2)}</strong>
      :<span key={i} style={{whiteSpace:"pre-wrap"}}>{part}</span>
  );

  return (
    <div>
      <div ref={ref} style={{fontSize:14,color:notes?"#ccc":"#444",lineHeight:1.7,minHeight:40,maxHeight:expanded?"none":"80px",overflow:"hidden",position:"relative"}}>
        {rendered}
        {!expanded&&overflow&&<div style={{position:"absolute",bottom:0,left:0,right:0,height:32,background:"linear-gradient(transparent,#141414)"}}/>}
      </div>
      {overflow&&<button onClick={()=>setExpanded(v=>!v)} style={{...btn("ghost",{padding:"4px 12px",fontSize:11,marginTop:8}),color:C.orange,borderColor:`${C.orange}44`}}>{expanded?"Vis mindre ↑":"Vis mer ↓"}</button>}
    </div>
  );
}

function InvitePanel({ project, users, user, onUpdate, onNotify, availability=[] }) {
  const [sending,setSending]=useState(null);
  const [toast,showToast]=useToast();
  const [showAdd,setShowAdd]=useState(false);

  const sendInvite=(uid)=>{
    setSending(uid);
    setTimeout(()=>{
      const cleanTeam=(project.team||[]).filter(x=>x!=null);
      const status = user?.role==="admin" && user?.id===uid ? "accepted" : "pending";
      const updated = {...project,invites:{...project.invites,[uid]:status},team:cleanTeam.includes(uid)?cleanTeam:[...cleanTeam,uid]};
      onUpdate(updated);
      if(status==="pending") onNotify(createNotification("invited", project, [uid]));
      setSending(null);
      showToast(status==="accepted"?`Du er lagt til på eventet!`:`Invitasjon sendt til ${users.find(x=>x.id===uid)?.name.split(" ")[0]}!`);
    },700);
  };

  const remind=uid=>showToast(`Påminnelse sendt til ${users.find(x=>x.id===uid)?.name.split(" ")[0]}!`);

  const isUnavailable = (uid) => {
    if(!project.date) return null;
    const projEnd = project.end_date || project.date;
    return availability.find(a =>
      a.user_id === uid &&
      project.date <= a.end_date &&
      projEnd >= a.start_date
    ) || null;
  };

  const notInvited=users.filter(u=>u.active&&!project.team.includes(u.id));

  return (
    <div style={{...card(),marginBottom:20}}>
      <Toast msg={toast.msg} ok={toast.ok}/>
      {lbl("Team og invitasjoner")}
      {project.team.map(uid=>{ const u=users.find(x=>x.id===uid); if(!u) return null; const st=project.invites?.[uid]||"pending"; return (
        <div key={uid} style={{display:"flex",alignItems:"center",gap:10,marginBottom:10,padding:"10px 12px",background:C.surface2,borderRadius:8,flexWrap:"wrap"}}>
          <Avatar initials={u.initials} size={32} role={u.role}/>
          <div style={{flex:1,minWidth:100}}><div style={{fontSize:13,fontWeight:500}}>{u.name}</div><div style={{fontSize:11,color:C.muted}}>{roleCfg[u.role]?.label}</div></div>
          {u.profile_info?.phone&&<a href={`tel:${u.profile_info.phone}`} style={{fontSize:11,padding:"4px 10px",borderRadius:8,background:"none",border:`0.5px solid ${C.blue}44`,color:C.blue,textDecoration:"none"}}>📞</a>}
          {isUnavailable(uid)&&<div style={{fontSize:11,color:C.red,marginTop:2}}>⚠ Opptatt – {isUnavailable(uid).label}</div>}
          <div style={ibadge(st)}>{inviteCfg[st].label}</div>
          {st==="pending"&&<button style={btn("warn",{padding:"5px 10px",fontSize:11})} onClick={()=>remind(uid)}>Påminn</button>}
          <button style={btn("danger",{padding:"5px 8px",fontSize:11})} onClick={()=>{
            const newTeam=project.team.filter(id=>id!==uid);
            const newInvites={...project.invites};
            delete newInvites[uid];
            onUpdate({...project,team:newTeam,invites:newInvites});
            onNotify(createNotification("removed",project,[uid]));
          }}>✕</button>
        </div>
      );})}
      {notInvited.length>0&&<>
        {showAdd&&<>
          <div style={{fontSize:11,color:C.muted,margin:"14px 0 10px",textTransform:"uppercase",letterSpacing:0.5}}>Legg til fra teamet</div>
          {notInvited.map(u=>(
            <div key={u.id} style={{display:"flex",alignItems:"center",gap:10,marginBottom:8,padding:"10px 12px",background:"#141414",borderRadius:8,border:`0.5px dashed ${C.border}`}}>
              <Avatar initials={u.initials} size={32} role={u.role}/>
              <div style={{flex:1,minWidth:80}}><div style={{fontSize:13,color:C.muted}}>{u.name}{isUnavailable(u.id)&&<span style={{fontSize:11,color:C.red,marginLeft:6}}>⚠ Opptatt</span>}</div><div style={{fontSize:11,color:"#444"}}>{roleCfg[u.role]?.label}</div></div>
              <button style={btn("primary",{padding:"5px 12px",fontSize:12})} onClick={()=>sendInvite(u.id)} disabled={sending===u.id}>{sending===u.id?"...":"+ Inviter"}</button>
            </div>
          ))}
        </>}
        <button style={{...btn("ghost",{width:"100%",marginTop:10,fontSize:13}),borderStyle:"dashed"}} onClick={()=>setShowAdd(v=>!v)}>
          {showAdd?"↑ Skjul":"+ Inviter deltaker"}
        </button>
      </>}
    </div>
  );
}

export function ProjectDetail({ project, user, users, customers=[], onCreateCustomer, onBack, onUpdate, onDelete, onNotify, availability=[], isMobile }) {
  const [editing,setEditing]=useState(false);
  const [draft,setDraft]=useState({...project});
  const [showHistory,setShowHistory]=useState(false);
  const [history,setHistory]=useState([]);
  const [loadingHistory,setLoadingHistory]=useState(false);
  const [autoSaved,setAutoSaved]=useState(false);
  const [repertoire,setRepertoire]=useState([]);
  const isAdmin=user.role==="admin";
  const canEdit = can(user,"editAllProjects") || (can(user,"editOwnProjects") && project.team.includes(user.id));
  const fmtDateTime = d => new Date(d+"Z").toLocaleDateString("nb-NO",{day:"numeric",month:"short",hour:"2-digit",minute:"2-digit"});
  const fmt=d=>new Date(d+"T12:00:00").toLocaleDateString("nb-NO",{day:"numeric",month:"long",year:"numeric"});
  const set=(k,v)=>setDraft(d=>({...d,[k]:v}));

  useEffect(()=>{
    if(!editing) return;
    const fn = e => { e.preventDefault(); e.returnValue = ""; };
    window.addEventListener("beforeunload", fn);
    return () => window.removeEventListener("beforeunload", fn);
  },[editing]);

  useEffect(()=>{
    if(!isAdmin) return;
    supabase.from("repertoire").select("*").then(({data})=>setRepertoire(data||[]));
  },[isAdmin]);

  useEffect(()=>{
    if(!editing) return;
    const timer = setTimeout(async()=>{
      await supabase.from('projects').upsert(draft);
      setAutoSaved(true);
      setTimeout(()=>setAutoSaved(false), 2000);
    }, 3000);
    return ()=>clearTimeout(timer);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  },[draft, editing]);

  const loadHistory = async () => {
    setLoadingHistory(true);
    const { data } = await supabase.from('project_history')
      .select('*')
      .eq('project_id', project.id)
      .order('created_at', {ascending:false})
      .limit(20);
    if(data) setHistory(data);
    setLoadingHistory(false);
    setShowHistory(true);
  };

  const restoreSnapshot = async (snapshot) => {
    if(!window.confirm("Er du sikker på at du vil gjenopprette denne versjonen?")) return;
    await supabase.from('projects').upsert(snapshot);
    onUpdate(snapshot);
    setShowHistory(false);
  };

  const save=async()=>{
    const recipients=project.team.filter(id=>id!==user.id&&project.invites?.[id]==="accepted");
    const changed=[];
    if(draft.name!==project.name)changed.push("navn");
    if(draft.date!==project.date||(draft.end_date||null)!==(project.end_date||null))changed.push("dato");
    if(draft.location!==project.location)changed.push("sted");
    if(draft.description!==project.description)changed.push("beskrivelse");
    if(draft.notes!==project.notes)changed.push("viktig info");
    if(JSON.stringify(draft.runsheet)!==JSON.stringify(project.runsheet))changed.push("kjøreplan");
    if(JSON.stringify(draft.files)!==JSON.stringify(project.files))changed.push("filer");
    if(JSON.stringify(draft.modules?.showprogram_data)!==JSON.stringify(project.modules?.showprogram_data))changed.push("show-program");
    if(JSON.stringify(draft.modules?.rehearsals_data)!==JSON.stringify(project.modules?.rehearsals_data))changed.push("øvingstider");
    if(JSON.stringify(draft.modules?.travel_data)!==JSON.stringify(project.modules?.travel_data))changed.push("reiseinfo");
    if(JSON.stringify(draft.modules?.accommodation_data)!==JSON.stringify(project.modules?.accommodation_data))changed.push("overnatting");
    if(draft.admin_notes!==project.admin_notes)changed.push("admin-notater");
    if(draft.address!==project.address)changed.push("adresse");
    if(draft.country!==project.country)changed.push("land");
    if(draft.client!==project.client)changed.push("kunde");
    if(draft.status!==project.status&&draft.status!=="confirmed")changed.push("status");
    if(draft.status==="confirmed"&&project.status!=="confirmed") onNotify(createNotification("confirmed",draft,recipients));
    else if(changed.length>0) onNotify(createNotification("updated",draft,recipients,{fields:changed.join(", ")}));
    if(changed.length>0) {
      await supabase.from('project_history').insert({
        project_id: project.id,
        changed_by: user.id,
        changed_by_name: user.name,
        snapshot: draft,
      });
    }
    onUpdate(draft); setEditing(false); setDraft({...draft});
  };

  return (
    <div style={{paddingBottom: isMobile?80:0}}>
      {showHistory&&(
        <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.75)",display:"flex",alignItems:"center",justifyContent:"center",zIndex:400,padding:16}} onClick={()=>setShowHistory(false)}>
          <div style={{background:C.surface,borderRadius:12,padding:24,width:"100%",maxWidth:480,maxHeight:"80vh",overflowY:"auto",border:`0.5px solid ${C.border}`}} onClick={e=>e.stopPropagation()}>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:16}}>
              <div style={{fontSize:16,fontWeight:500}}>Endringslogg</div>
              <button onClick={()=>setShowHistory(false)} style={{background:"none",border:"none",color:C.muted,fontSize:22,cursor:"pointer"}}>×</button>
            </div>
            {loadingHistory&&<div style={{color:C.muted,fontSize:13,textAlign:"center",padding:20}}>Laster...</div>}
            {!loadingHistory&&history.length===0&&<div style={{color:C.muted,fontSize:13,textAlign:"center",padding:20}}>Ingen historikk ennå.</div>}
            {history.map((h,i)=>(
              <div key={h.id} style={{...card({marginBottom:8}),display:"flex",alignItems:"center",gap:12}}>
                <div style={{flex:1}}>
                  <div style={{fontSize:13,fontWeight:500}}>{h.changed_by_name}</div>
                  <div style={{fontSize:11,color:C.muted,marginTop:2}}>{fmtDateTime(h.created_at)}</div>
                </div>
                {i>0&&<button style={btn("ghost",{padding:"4px 10px",fontSize:11})} onClick={()=>restoreSnapshot(h.snapshot)}>Gjenopprett</button>}
                {i===0&&<div style={{fontSize:11,color:C.green,padding:"4px 10px"}}>Nyeste</div>}
              </div>
            ))}
          </div>
        </div>
      )}
      {gradBar}
      {project.invites?.[user.id]==="pending" && (
        <div style={{display:"flex",alignItems:"center",gap:14,padding:"14px 16px",borderRadius:10,border:`0.5px solid ${C.gold}55`,background:"#1a1508",marginBottom:20,flexWrap:"wrap"}}>
          <div style={{width:32,height:32,borderRadius:"50%",background:`${C.gold}22`,display:"flex",alignItems:"center",justifyContent:"center",fontSize:16,color:C.gold,flexShrink:0}}>!</div>
          <div style={{flex:1,minWidth:120}}>
            <div style={{fontSize:13,fontWeight:500,color:C.white}}>Du er invitert til dette oppdraget</div>
            <div style={{fontSize:11,color:C.muted,marginTop:2}}>Vennligst svar på invitasjonen</div>
          </div>
          <div style={{display:"flex",gap:8}}>
            <button style={btn("success",{padding:"7px 14px",fontSize:12})} onClick={()=>{ onUpdate({...project,invites:{...project.invites,[user.id]:"accepted"}}); logActivity(user,"invite_accepted",{event_name:project.name},"detail"); }}>✓ Aksepter</button>
            <button style={btn("danger",{padding:"7px 14px",fontSize:12})} onClick={()=>{ onUpdate({...project,invites:{...project.invites,[user.id]:"declined"}}); logActivity(user,"invite_declined",{event_name:project.name},"detail"); }}>✗ Avvis</button>
          </div>
        </div>
      )}
      {editing&&<div style={{position:"fixed",top:isMobile?"calc(53px + env(safe-area-inset-top))":0,left:isMobile?0:240,right:0,zIndex:100,background:`linear-gradient(135deg,#1a0800,#0e0e0e)`,padding:`16px ${isMobile?"16px":"28px"}`,display:"flex",justifyContent:"space-between",alignItems:"center",gap:12}}>
        <div style={{position:"absolute",bottom:0,left:0,right:0,height:2,background:`linear-gradient(90deg,${C.orange},${C.magenta},${C.gold})`}}/>
        <div style={{display:"flex",alignItems:"center",gap:8,minWidth:0}}>
          <button onClick={onBack} style={btn("ghost",{fontSize:12,padding:"5px 10px"})}>← Tilbake</button>
          <div style={{display:"flex",alignItems:"center",gap:6}}>
            <div style={{width:6,height:6,borderRadius:"50%",background:C.orange,boxShadow:`0 0 6px ${C.orange}`,flexShrink:0}}/>
            <div style={{fontSize:12,color:C.orange,fontWeight:500,letterSpacing:0.5,textTransform:"uppercase"}}>Redigerer</div>
          </div>
          {!isMobile&&<div style={{fontSize:13,color:"#aaa",overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{draft.name}</div>}
        </div>
        <div style={{display:"flex",gap:6,alignItems:"center",flexShrink:0}}>
          {autoSaved
            ?<div style={{fontSize:11,color:C.green,display:"flex",alignItems:"center",gap:4}}><div style={{width:5,height:5,borderRadius:"50%",background:C.green}}/>Lagret</div>
            :<div style={{fontSize:11,color:"#555"}}>Autolagrer...</div>
          }
          <button onClick={()=>{setDraft({...project});setEditing(false);}} style={btn("ghost",{fontSize:12,padding:"5px 10px"})}>Avbryt</button>
          <button onClick={save} style={btn("primary",{fontSize:12,padding:"5px 14px"})}>Lagre</button>
        </div>
      </div>}
      {editing&&<div style={{height:52}}/>}
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:20}}>
        <button onClick={onBack} style={btn("ghost",{fontSize:13})}>← Tilbake</button>
        {canEdit&&!editing&&<div style={{display:"flex",gap:8}}>
          {isAdmin&&<button style={btn("ghost")} onClick={loadHistory}>Historikk</button>}
          <button style={btn("ghost")} onClick={()=>{setDraft({...project});setEditing(true);}}>Rediger event</button>
          {!project.archived&&<button style={btn("ghost",{fontSize:13})} onClick={()=>onUpdate({...project,archived:true})}>Arkiver event</button>}
          {project.archived&&<button style={btn("danger",{fontSize:13})} onClick={()=>{ if(window.prompt(`Skriv DELETE for å slette «${project.name}» permanent:`)===`DELETE`) onDelete(project.id); }}>Slett permanent</button>}
          {project.archived&&<button style={btn("ghost",{fontSize:13})} onClick={()=>onUpdate({...project,archived:false})}>Gjenopprett</button>}
        </div>}
      </div>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:20,gap:12}}>
        <div style={{flex:1,minWidth:0}}>
          <div style={{fontSize:11,color:C.muted,fontFamily:"monospace",marginBottom:4}}>#{project.id}</div>
          {editing?<input style={inp({fontSize:16,fontWeight:500,marginBottom:6})} value={draft.name} onChange={e=>set("name",e.target.value)}/>:<div style={{fontSize:20,fontWeight:500,marginBottom:4}}>{project.name}</div>}
          {editing
            ? <CustomerSelect customers={customers} value={draft.customer_id} onCreate={onCreateCustomer}
                onChange={id=>{ set("customer_id",id); set("client",customers.find(c=>c.id===id)?.name||""); }}/>
            : <div style={{fontSize:13,color:C.muted}}>{project.client}</div>}
        </div>
        {editing?<select style={inp({width:140,flexShrink:0})} value={draft.status} onChange={e=>set("status",e.target.value)}>{Object.entries(statusCfg).map(([k,v])=><option key={k} value={k}>{v.label}</option>)}</select>:<div style={badge(project.status)}>{statusCfg[project.status]?.label}</div>}
      </div>
      {can(user,"seeAdminNotes")&&<div style={{...card({marginBottom:16,background:"#141414",border:`0.5px solid ${C.orange}22`})}}>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:8}}>
          <div style={{fontSize:11,color:C.orange,textTransform:"uppercase",letterSpacing:0.5,display:"flex",alignItems:"center",gap:6}}>
            <div style={{width:6,height:6,borderRadius:"50%",background:C.orange}}/>Admin-notater
          </div>
          <div style={{fontSize:10,color:"#444"}}>Kun synlig for admin</div>
        </div>
        {editing
          ?<textarea style={inp({minHeight:60,resize:"vertical",background:"#0e0e0e"})} value={draft.admin_notes||""} onChange={e=>set("admin_notes",e.target.value)} placeholder="Interne notater, honorar, kontaktinfo..."/>
          :<AdminNotesView notes={project.admin_notes}/>}
      </div>}
      {can(user,"seeAdminNotes")&&<div style={{...card({marginBottom:16,background:"#0f0f12",border:`0.5px solid #60a5fa22`})}}>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:8}}>
          <div style={{fontSize:11,color:"#60a5fa",textTransform:"uppercase",letterSpacing:0.5,display:"flex",alignItems:"center",gap:6}}>
            <div style={{width:6,height:6,borderRadius:"50%",background:"#60a5fa"}}/>To-dos
          </div>
          <div style={{fontSize:10,color:"#444"}}>Kun synlig for admin</div>
        </div>
        <TodoEditor
          todos={editing?draft.todos||[]:project.todos||[]}
          onChange={v=>{ if(editing){ set("todos",v); } else { const updated={...project,todos:v}; onUpdate(updated); } }}
          editing={true}
          currentUser={user}
          project={project}
          onToggle={todoId=>{
            const todo=(project.todos||[]).find(t=>t.id===todoId);
            const updated={...project,todos:(project.todos||[]).map(t=>t.id===todoId?{...t,done:!t.done}:t)};
            onUpdate(updated);
            if(todo) logActivity(user, 'todo_completed', {todo_text:todo.title, event_name:project.name}, 'detail');
          }}
        />
      </div>}
      <div style={{display:"grid",gridTemplateColumns:isMobile?"1fr":(!editing&&(project.modules?.rehearsals_data||[]).length>0?"1fr 1fr 2fr":"1fr 1fr"),gap:12,marginBottom:16}}>
        <div style={card()}>{lbl("Dato")}{editing?<div style={{display:"flex",flexDirection:"column",gap:8}}><input style={inp()} type="date" value={draft.date} onChange={e=>set("date",e.target.value)}/>{lbl("Til dato (flerdagers)")}<input style={inp()} type="date" min={draft.date||undefined} value={draft.end_date||""} onChange={e=>set("end_date",e.target.value||null)}/></div>:<div style={{fontSize:14,fontWeight:500}}>{fmt(project.date)}{project.end_date&&project.end_date!==project.date?` – ${fmt(project.end_date)}`:""}</div>}</div>
        <div style={card()}>{lbl("Sted")}{editing?<div style={{display:"flex",flexDirection:"column",gap:8}}><input style={inp()} value={draft.location} onChange={e=>set("location",e.target.value)} placeholder="Venue / sted"/><input style={inp()} value={draft.address||""} onChange={e=>set("address",e.target.value)} placeholder="Adresse"/><input style={inp()} value={draft.country||""} onChange={e=>set("country",e.target.value)} placeholder="Land"/></div>:<div><div style={{fontSize:14,fontWeight:500}}>{project.location}</div>{project.address&&<div style={{fontSize:13,color:C.muted,marginTop:2}}>{project.address}</div>}{project.country&&<div style={{fontSize:13,color:C.muted,marginTop:2}}>{project.country}</div>}</div>}</div>
        {!editing&&(project.modules?.rehearsals_data||[]).length>0&&<div style={card()}>
          {lbl("Øvingstider")}
          <div style={{display:"flex",flexDirection:"column",gap:6}}>
            {(project.modules.rehearsals_data).map(r=>(
              <div key={r.id} style={{display:"flex",alignItems:"center",gap:10,flexWrap:"wrap"}}>
                {r.date&&(()=>{const d=new Date(r.date+"T12:00:00");const day=d.toLocaleDateString("nb-NO",{day:"numeric"});const mon=d.toLocaleDateString("nb-NO",{month:"short"}).toUpperCase();return <div style={{width:36,height:36,borderRadius:8,background:`${C.magenta}22`,border:`0.5px solid ${C.magenta}44`,display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",flexShrink:0}}><div style={{fontSize:8,color:C.magenta,fontWeight:600,letterSpacing:0.5}}>{mon}</div><div style={{fontSize:14,color:C.white,fontWeight:700,lineHeight:1}}>{day}</div></div>;})()}
                <div>
                  <div style={{fontFamily:"monospace",fontSize:13,color:C.white}}>{r.timeFrom}{r.timeTo?` – ${r.timeTo}`:""}{r.location&&<span style={{fontFamily:"sans-serif",fontSize:12,color:C.muted}}> · {r.location}</span>}</div>
                  {r.comment&&<div style={{fontSize:12,color:C.muted,marginTop:2}}>{r.comment}</div>}
                </div>
              </div>
            ))}
          </div>
        </div>}
      </div>
      <div style={{...card(),marginBottom:16}}>{lbl("Beskrivelse")}{editing?<textarea style={inp({minHeight:70,resize:"vertical"})} value={draft.description} onChange={e=>set("description",e.target.value)}/>:<div style={{fontSize:14,color:"#ccc",lineHeight:1.6,whiteSpace:"pre-wrap"}}>{project.description||<span style={{color:"#444"}}>Ingen beskrivelse.</span>}</div>}</div>
      <div style={{...card(),marginBottom:16}}>{lbl("Viktig info / ta med")}{editing?<textarea style={inp({minHeight:60,resize:"vertical"})} value={draft.notes} onChange={e=>set("notes",e.target.value)} placeholder="Kostyme, parkering, kontakt..."/>:<div style={{fontSize:14,color:project.notes?"#ccc":"#444",lineHeight:1.6,whiteSpace:"pre-wrap"}}>{project.notes||"Ingen notater."}</div>}</div>
      {isAdmin&&editing&&<div style={{...card({marginBottom:16})}}>
        {lbl("Aktive moduler")}
        <div style={{display:"flex",flexWrap:"wrap",gap:8,marginTop:4}}>
          {Object.entries(MODULE_CFG).map(([k,v])=>{
            const active=(draft.modules||{})[k];
            return <div key={k} onClick={()=>setDraft(d=>({...d,modules:{...(d.modules||{}),[k]:!active}}))} style={{display:"flex",alignItems:"center",gap:6,padding:"6px 12px",borderRadius:20,cursor:"pointer",fontSize:13,border:`0.5px solid ${active?C.orange:C.border}`,background:active?"#FF5F1F22":"transparent",color:active?C.orange:C.muted}}>{v.icon} {v.label}</div>;
          })}
        </div>
      </div>}
      {((editing?draft:project).modules||{}).showprogram&&<div style={{...card(),marginBottom:16}}>
        {lbl("🎭 Show-program")}
        {editing
          ?<ShowProgramEditor
              items={draft.modules?.showprogram_data||[]}
              onChange={v=>setDraft(d=>({...d,modules:{...d.modules,showprogram_data:v}}))}
              teamUsers={users.filter(u=>(draft.team||[]).includes(u.id))}
            />
          :<ShowProgramViewer
              items={project.modules?.showprogram_data||[]}
              teamUsers={users.filter(u=>(project.team||[]).includes(u.id))}
            />
        }
      </div>}
      <div style={{...card(),marginBottom:16}}>
        {lbl("Kjøreplan")}
        {editing
          ? <RunDownEditor runsheet={draft.runsheet} onChange={v=>set("runsheet",v)} eventDate={draft.date||""} showProgramItems={draft.modules?.showprogram_data||[]}/>
          : <RunDownViewer runsheet={project.runsheet} eventDate={project.date||""} isMobile={isMobile} showProgramItems={project.modules?.showprogram_data||[]} teamUsers={users.filter(u=>(project.team||[]).includes(u.id))}/>
        }
      </div>
      <div style={{...card(),marginBottom:16}}>
        {lbl("Dokumenter og filer")}
        {(editing?draft:project).files.length===0&&!editing&&<div style={{fontSize:13,color:"#444"}}>Ingen filer lagt til.</div>}
        {editing?<FilesEditor files={draft.files} onChange={v=>set("files",v)}/>:project.files.map(f=>(
          <div key={f.id} style={{display:"flex",alignItems:"center",gap:10,marginBottom:8,padding:"10px 12px",background:C.surface2,borderRadius:8}}>
            <div style={{fontSize:11,padding:"2px 6px",borderRadius:20,background:`${f.type==="drive"?"#4ade80":f.type==="docs"?"#60a5fa":f.type==="sheet"?"#F5A623":"#666"}22`,color:f.type==="drive"?"#4ade80":f.type==="docs"?"#60a5fa":f.type==="sheet"?"#F5A623":"#666",border:`0.5px solid ${f.type==="drive"?"#4ade80":f.type==="docs"?"#60a5fa":f.type==="sheet"?"#F5A623":"#666"}44`}}>{f.type==="drive"?"Drive":f.type==="docs"?"Docs":f.type==="sheet"?"Sheet":"Lenke"}</div>
            <div style={{flex:1,fontSize:13,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{f.name}</div>
            <a href={f.url} target="_blank" rel="noreferrer" style={{fontSize:12,color:C.orange,textDecoration:"none"}}>Åpne ↗</a>
          </div>
        ))}
      </div>
      {((editing?draft:project).modules||{}).rehearsals&&<div style={{...card(),marginBottom:16}}>
        {lbl("🕐 Øvingstider")}
        {editing
          ?<RehearsalEditor items={draft.modules?.rehearsals_data||[]} onChange={v=>setDraft(d=>({...d,modules:{...d.modules,rehearsals_data:v}}))}/>
          :(project.modules?.rehearsals_data||[]).length===0
            ?<div style={{fontSize:13,color:"#444"}}>Ingen øvingstider lagt til.</div>
            :(project.modules?.rehearsals_data||[]).map(r=>(
              <div key={r.id} style={{marginBottom:8,padding:"10px 12px",background:C.surface2,borderRadius:8,borderLeft:`3px solid ${C.magenta}`}}>
                <div style={{display:"flex",alignItems:"center",gap:8,flexWrap:"wrap"}}>
                  <div style={{fontFamily:"monospace",fontSize:12,color:C.white,minWidth:72}}>{r.date?new Date(r.date+"T12:00:00").toLocaleDateString("nb-NO",{day:"numeric",month:"short"}):""}</div>
                  <div style={{fontFamily:"monospace",fontSize:12,color:C.white}}>{r.timeFrom}{r.timeTo?` – ${r.timeTo}`:""}</div>
                  {r.location&&<div style={{fontSize:13,color:"#ccc",flex:1}}>{r.location}</div>}
                </div>
                {r.comment&&<div style={{fontSize:12,color:C.muted,marginTop:4}}>{r.comment}</div>}
              </div>
            ))
        }
      </div>}
      {((editing?draft:project).modules||{}).travel&&<div style={{...card(),marginBottom:16}}>
        {lbl("✈️ Reise og transport")}
        {editing
          ?<TravelEditor value={draft.modules?.travel_data||""} onChange={v=>setDraft(d=>({...d,modules:{...d.modules,travel_data:v}}))}/>
          :<div style={{fontSize:14,color:project.modules?.travel_data?"#ccc":"#444",lineHeight:1.6,whiteSpace:"pre-wrap"}}>{project.modules?.travel_data||"Ingen reiseinfo lagt til."}</div>
        }
      </div>}
      {((editing?draft:project).modules||{}).accommodation&&<div style={{...card(),marginBottom:16}}>
        {lbl("🏨 Overnatting")}
        {editing
          ?<textarea style={inp({minHeight:80,resize:"vertical"})} value={draft.modules?.accommodation_data||""} onChange={e=>setDraft(d=>({...d,modules:{...d.modules,accommodation_data:e.target.value}}))} placeholder="Hotellnavn, adresse, innsjekkinfo..."/>
          :<div style={{fontSize:14,color:project.modules?.accommodation_data?"#ccc":"#444",lineHeight:1.6,whiteSpace:"pre-wrap"}}>{project.modules?.accommodation_data||"Ingen overnattingsinfo."}</div>
        }
      </div>}
      {((editing?draft:project).modules||{}).costume&&<div style={{...card(),marginBottom:16}}>
        {lbl("👗 Kostyme")}
        {editing
          ?<textarea style={inp({minHeight:80,resize:"vertical"})} value={draft.modules?.costume_data||""} onChange={e=>setDraft(d=>({...d,modules:{...d.modules,costume_data:e.target.value}}))} placeholder="Kostymeinfo, farger, instruksjoner..."/>
          :<div style={{fontSize:14,color:project.modules?.costume_data?"#ccc":"#444",lineHeight:1.6,whiteSpace:"pre-wrap"}}>{project.modules?.costume_data||"Ingen kostymeinformasjon."}</div>
        }
      </div>}
      {isAdmin&&!editing&&(()=>{
        const showItems = project.modules?.showprogram_data||[];
        const repoItems = showItems.map(s=>repertoire.find(r=>r.id===s.repertoireId||r.title===s.title)).filter(Boolean);
        const ALL_LINKS = [
          {key:"instrumental", label:"Instrumental"},
          {key:"klikktrack",   label:"Klikktrack"},
          {key:"choir",        label:"Choir-track"},
          {key:"acapella",     label:"Acapella"},
          {key:"outro",        label:"Outro"},
          {key:"visuals",      label:"Visuals"},
          {key:"tc",           label:"TC fil (lys)"},
          {key:"koreografi",   label:"Koreografi (video)"},
          {key:"showzip",      label:"Show ZIP"},
        ];
        const hasAnyLinks = repoItems.some(r=>r.links&&Object.values(r.links).some(v=>v));
        if(!hasAnyLinks) return null;
        return (
          <div style={{...card({marginBottom:16,border:`0.5px solid ${C.orange}22`})}}>
            {lbl("🎵 Repertoire-filer (kun admin)")}
            {repoItems.map(r=>{
              const links = ALL_LINKS.filter(({key})=>r.links?.[key]);
              if(links.length===0) return null;
              return (
                <div key={r.id} style={{marginBottom:12}}>
                  <div style={{fontSize:13,fontWeight:500,color:C.white,marginBottom:6}}>{r.title}</div>
                  <div style={{display:"flex",flexDirection:"column",gap:4}}>
                    {links.map(({key,label})=>(
                      <div key={key} style={{display:"flex",alignItems:"center",justifyContent:"space-between",padding:"7px 12px",background:C.surface2,borderRadius:8}}>
                        <div style={{fontSize:12,color:C.muted}}>{label}</div>
                        <div style={{display:"flex",gap:6}}>
                          <a href={r.links[key]} target="_blank" rel="noreferrer" style={{fontSize:11,padding:"4px 10px",borderRadius:8,background:"none",border:`0.5px solid ${C.border}`,color:C.muted,textDecoration:"none"}}>↗ Åpne</a>
                          <a href={(()=>{ const m=r.links[key].match(/\/d\/([a-zA-Z0-9_-]+)/u); return m?`https://drive.google.com/uc?export=download&id=${m[1]}`:r.links[key]; })()} target="_blank" rel="noreferrer" style={{fontSize:11,padding:"4px 10px",borderRadius:8,background:"none",border:`0.5px solid ${C.border}`,color:C.muted,textDecoration:"none"}}>⬇ Last ned</a>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        );
      })()}
      {isAdmin&&((editing?draft:project).modules||{}).contract&&<div style={{...card({marginBottom:16,border:`0.5px solid ${C.gold}22`})}}>
        {lbl("📋 Kontrakt og betaling (kun admin)")}
        {editing
          ?<textarea style={inp({minHeight:80,resize:"vertical"})} value={draft.modules?.contract_data||""} onChange={e=>setDraft(d=>({...d,modules:{...d.modules,contract_data:e.target.value}}))} placeholder="Honorar, betalingsfrist, kontraktsnotater..."/>
          :<div style={{fontSize:14,color:project.modules?.contract_data?"#ccc":"#444",lineHeight:1.6,whiteSpace:"pre-wrap"}}>{project.modules?.contract_data||"Ingen kontraktinfo."}</div>
        }
      </div>}
      {can(user,"invitePeople")
        ? <InvitePanel project={editing?draft:project} users={users} user={user} onUpdate={p=>{onUpdate(p);setDraft(p);}} onNotify={onNotify} availability={availability}/>
        : <div style={card()}>
            {lbl("Team")}
            {project.team.map(uid=>{ const u=users.find(x=>x.id===uid); if(!u) return null; const st=project.invites?.[uid]||"pending"; return (
              <div key={uid} style={{display:"flex",alignItems:"center",gap:12,marginBottom:12}}>
                <Avatar initials={u.initials} size={32} role={u.role}/>
                <div style={{flex:1}}>
                  <div style={{fontSize:13,fontWeight:500}}>{u.name}</div>
                  <div style={{fontSize:11,color:C.muted}}>{roleCfg[u.role]?.label}</div>
                  {u.profile_info?.phone&&<a href={`tel:${u.profile_info.phone}`} style={{fontSize:11,color:C.blue,textDecoration:"none",marginTop:2,display:"block"}}>{u.profile_info.phone}</a>}
                </div>
                <div style={ibadge(st)}>{inviteCfg[st]?.label}</div>
              </div>
            );})}
          </div>}
    </div>
  );
}